import type { GrowProps } from "@mui/material/Grow";
import { useTheme } from "@mui/material/styles";
import { useForkRef } from "@mui/material/utils";
import {
  cloneElement,
  forwardRef,
  useRef,
  type CSSProperties,
  type ReactElement,
  type Ref,
  type RefObject,
} from "react";
import { Transition, type TransitionStatus } from "react-transition-group";

const ENTER_START_TRANSFORM = "scale(0.9) translateY(0)";
const ENTER_END_TRANSFORM = "scale(1) translateY(0)";
const EXIT_END_TRANSFORM = "scale(0.8) translateY(0)";
const DEFAULT_ENTER_DURATION_MS = 350;
const DEFAULT_EXIT_DURATION_MS = 200;
const DEFAULT_TIMEOUT = {
  enter: DEFAULT_ENTER_DURATION_MS,
  exit: DEFAULT_EXIT_DURATION_MS,
};
const DEFAULT_ENTER_EASING = "cubic-bezier(0, 0.91, 0, 1)";
const DEFAULT_EXIT_EASING = "cubic-bezier(0.59, 0, 1, 0.375)";
type ResolvedTransitionTimeout = number | { appear?: number; enter?: number; exit?: number };

const styles: Record<TransitionStatus, CSSProperties> = {
  entering: {
    opacity: 1,
    transform: ENTER_END_TRANSFORM,
  },
  entered: {
    opacity: 1,
    transform: ENTER_END_TRANSFORM,
  },
  exiting: {
    opacity: 0,
    transform: EXIT_END_TRANSFORM,
  },
  exited: {
    opacity: 0,
    transform: ENTER_START_TRANSFORM,
  },
  unmounted: {},
};

const hiddenStyles: CSSProperties = {
  opacity: 0,
  transform: ENTER_START_TRANSFORM,
  visibility: "hidden",
};

type TransitionHandler = (node: HTMLElement, isAppearing?: boolean) => void;

function reflow(node: Element) {
  return node.scrollTop;
}

function getTransitionProps(
  props: Pick<GrowProps, "easing" | "style"> & { timeout: ResolvedTransitionTimeout },
  mode: "enter" | "exit",
) {
  const { timeout, easing, style = {} } = props;
  const duration = typeof timeout === "number" ? timeout : (timeout[mode] ?? 0);

  return {
    delay: style.transitionDelay,
    duration: style.transitionDuration ?? duration,
    easing: typeof easing === "object" ? easing?.[mode] : easing,
  };
}

function getDurationInMs(duration: string | number) {
  if (typeof duration === "number") {
    return duration;
  }

  const normalizedDuration = duration.split(",")[0]?.trim().toLowerCase();

  if (!normalizedDuration) {
    return 0;
  }

  const parseDuration = (value: string, multiplier = 1) => {
    const parsedDuration = Number.parseFloat(value);
    return Number.isFinite(parsedDuration) ? parsedDuration * multiplier : 0;
  };

  if (normalizedDuration.endsWith("ms")) {
    return parseDuration(normalizedDuration.slice(0, -2));
  }
  if (normalizedDuration.endsWith("s")) {
    return parseDuration(normalizedDuration.slice(0, -1), 1000);
  }

  return parseDuration(normalizedDuration);
}

function getTransitionChildStyle(
  state: TransitionStatus,
  inProp: boolean | undefined,
  styleProp: CSSProperties | undefined,
  childStyle: CSSProperties | undefined,
) {
  const base = state === "exited" && !inProp ? hiddenStyles : (styles[state] ?? styles.exited);

  if (!styleProp && !childStyle) {
    return base;
  }

  return {
    ...base,
    ...styleProp,
    ...childStyle,
  };
}

function normalizedTransitionCallback(
  nodeRef: RefObject<HTMLElement | null>,
  callback: TransitionHandler | undefined,
) {
  return (maybeIsAppearing?: boolean) => {
    if (!callback) {
      return;
    }

    const node = nodeRef.current;

    if (!node) {
      return;
    }

    if (maybeIsAppearing === undefined) {
      callback(node);
      return;
    }

    callback(node, maybeIsAppearing);
  };
}

export const SrPopoverGrow = forwardRef(function SrPopoverGrow(
  {
    addEndListener,
    appear = true,
    children,
    easing,
    in: inProp,
    onEnter,
    onEntered,
    onEntering,
    onExit,
    onExited,
    onExiting,
    style,
    timeout,
    ref: _transitionRef,
    ...other
  }: GrowProps,
  ref: Ref<unknown>,
) {
  void _transitionRef;
  const theme = useTheme();
  const nodeRef = useRef<HTMLElement | null>(null);
  const child = children as ReactElement<{ style?: CSSProperties; ref?: Ref<HTMLElement> }>;
  // React 19 passes ref as a prop; reading element.ref logs a removal warning.
  const childRef = child.props.ref;
  const handleRef = useForkRef(useForkRef(nodeRef, childRef), ref);
  const resolvedTimeout: ResolvedTransitionTimeout =
    timeout && timeout !== "auto" ? timeout : DEFAULT_TIMEOUT;

  const handleEnter = normalizedTransitionCallback(nodeRef, (node, isAppearing) => {
    reflow(node);

    const {
      delay,
      duration: transitionDuration,
      easing: transitionTimingFunction,
    } = getTransitionProps({ easing, style, timeout: resolvedTimeout }, "enter");
    const duration = getDurationInMs(transitionDuration);

    node.style.transition = [
      theme.transitions.create("opacity", {
        delay,
        duration: 0,
      }),
      theme.transitions.create("transform", {
        delay,
        duration,
        easing: transitionTimingFunction || DEFAULT_ENTER_EASING,
      }),
    ].join(",");

    (onEnter as TransitionHandler | undefined)?.(node, isAppearing);
  });

  const handleEntering = normalizedTransitionCallback(
    nodeRef,
    onEntering as TransitionHandler | undefined,
  );
  const handleEntered = normalizedTransitionCallback(
    nodeRef,
    onEntered as TransitionHandler | undefined,
  );

  const handleExit = normalizedTransitionCallback(nodeRef, (node) => {
    const {
      delay,
      duration: transitionDuration,
      easing: transitionTimingFunction,
    } = getTransitionProps({ easing, style, timeout: resolvedTimeout }, "exit");
    const duration = getDurationInMs(transitionDuration);

    node.style.transition = [
      theme.transitions.create("opacity", {
        delay,
        duration,
      }),
      theme.transitions.create("transform", {
        delay,
        duration,
        easing: transitionTimingFunction || DEFAULT_EXIT_EASING,
      }),
    ].join(",");
    node.style.opacity = "0";
    node.style.transform = EXIT_END_TRANSFORM;

    (onExit as TransitionHandler | undefined)?.(node);
  });

  const handleExiting = normalizedTransitionCallback(
    nodeRef,
    onExiting as TransitionHandler | undefined,
  );
  const handleExited = normalizedTransitionCallback(nodeRef, (node) => {
    node.style.transition = "";
    (onExited as TransitionHandler | undefined)?.(node);
  });

  const handleAddEndListener = (next: () => void) => {
    if (nodeRef.current) {
      addEndListener?.(nodeRef.current, next);
    }
  };

  return (
    <Transition
      appear={appear}
      in={inProp}
      nodeRef={nodeRef}
      onEnter={handleEnter}
      onEntered={handleEntered}
      onEntering={handleEntering}
      onExit={handleExit}
      onExited={handleExited}
      onExiting={handleExiting}
      addEndListener={handleAddEndListener}
      timeout={resolvedTimeout}
      {...other}
    >
      {(state, restChildProps = {}) => {
        const childStyle = getTransitionChildStyle(state, inProp, style, child.props.style);

        return cloneElement(child as ReactElement<Record<string, unknown>>, {
          ...(restChildProps as Record<string, unknown>),
          ref: handleRef,
          style: childStyle,
        });
      }}
    </Transition>
  );
});
